import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import { validateNavigationItem } from "../constants/navigation";

/**
 * Custom hook for navigation state and actions
 */
export const useNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const lastExpandedPath = useRef(null);

  // Check if a path is currently active
  const isActive = useCallback(
    (path) => {
      if (!path) return false;
      return location.pathname === path;
    },
    [location.pathname]
  );

  // Check if an item has an active child
  const hasActiveChild = useCallback(
    (item) => {
      if (!item.children) return false;
      return item.children.some((child) => isActive(child.path));
    },
    [isActive]
  );

  // Check if an item should be expanded
  const isExpanded = useCallback(
    (item) => {
      if (!item.children) return false;
      return expandedItems.has(item.text);
    },
    [expandedItems]
  );

  // Toggle expansion state
  const toggleExpanded = useCallback((item) => {
    if (!item.children) return;

    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(item.text)) {
        newSet.delete(item.text);
      } else {
        newSet.add(item.text);
      }
      return newSet;
    });
  }, []);

  // Auto-expand parent items when they are selected
  const autoExpandParent = useCallback((item) => {
    if (!item.children) return;

    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      newSet.add(item.text);
      return newSet;
    });
  }, []);

  // Auto-expand items with active children on mount
  const autoExpandActiveItems = useCallback(
    (items) => {
      const currentPath = location.pathname;

      // Only expand if the path has changed
      if (lastExpandedPath.current === currentPath) {
        return;
      }

      lastExpandedPath.current = currentPath;
      const itemsToExpand = new Set();

      const checkItems = (itemList) => {
        itemList.forEach((item) => {
          if (item.children) {
            // Check if any child is active using current path directly
            const hasActiveChild = item.children.some(
              (child) => child.path === currentPath
            );
            if (hasActiveChild) {
              itemsToExpand.add(item.text);
            }
            // Recursively check children
            checkItems(item.children);
          }
        });
      };

      checkItems(items);

      if (itemsToExpand.size > 0) {
        setExpandedItems((prev) => {
          const newSet = new Set(prev);
          itemsToExpand.forEach((item) => newSet.add(item));
          return newSet;
        });
      }
    },
    [location.pathname]
  );

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [navigate]);

  // Handle navigation item click
  const handleItemClick = useCallback(
    (item, event) => {
      if (event && event.preventDefault) {
        event.preventDefault();
      }

      // Validate the navigation item
      if (!validateNavigationItem(item)) {
        return;
      }

      // Handle different item types
      switch (item.type) {
        case "route":
          if (item.path) {
            navigate(item.path);
            // Auto-expand parent items when they are selected
            if (item.children) {
              autoExpandParent(item);
            }
          }
          break;
        case "external":
          // Handle external links
          if (item.onClick) {
            item.onClick(event);
          }
          break;
        case "action":
          if (item.action === "logout") {
            handleLogout();
          } else if (item.onClick) {
            item.onClick(event);
          }
          break;
        default:
          if (item.onClick) {
            item.onClick(event);
          }
      }

      // Toggle expansion for items with children (only if not auto-expanding)
      if (item.children && item.type !== "route") {
        toggleExpanded(item);
      }
    },
    [navigate, handleLogout, toggleExpanded, autoExpandParent]
  );

  // Memoized navigation state
  const navigationState = useMemo(
    () => ({
      isActive,
      hasActiveChild,
      isExpanded,
      toggleExpanded,
      autoExpandParent,
      autoExpandActiveItems,
      handleItemClick,
      handleLogout,
    }),
    [
      isActive,
      hasActiveChild,
      isExpanded,
      toggleExpanded,
      autoExpandParent,
      handleItemClick,
      handleLogout,
      autoExpandActiveItems,
    ]
  );

  return navigationState;
};

// Admin module navigation hook removed - admin is now integrated into main app
